/**
 * Customer-facing second-screen channel (the `customer_display` module).
 *
 * The POS register publishes a snapshot of the live sale; a display window
 * opened on a second monitor (same browser, same origin) renders it in real
 * time. Transport is BroadcastChannel — no backend round-trip — with a
 * localStorage mirror so a freshly opened display shows the current cart
 * immediately and a `storage` event fallback covers browsers/quirks where
 * BroadcastChannel misses.
 */

const CHANNEL = 'erp-customer-display';
const STORAGE_KEY = 'erp-customer-display:last';

export interface DisplayLine {
  id: string;
  name: string;
  unitName?: string | null;
  quantity: string;
  unitPrice: string;
  lineTotal: string;
  itemId?: string | null;
  /**
   * The product photo's ATTACHMENT id. Populated by the offline till, whose
   * display resolves images out of a local blob cache keyed that way; the web
   * register leaves it unset and the web display ignores it (it resolves photos
   * from the item id instead). Optional in both directions, so either register
   * can drive either display.
   */
  imageId?: string | null;
}

export interface DisplaySnapshot {
  /** 'idle' = no active sale (welcome screen); 'sale' = live cart; 'done' = finalized. */
  state: 'idle' | 'sale' | 'done';
  company: string;
  currency: string;
  locale: string;
  customerName?: string | null;
  lines: DisplayLine[];
  subtotal: string;
  taxTotal: string;
  discountTotal: string;
  total: string;
  /** Set on 'done' so the screen can show the change owed back. */
  changeDue?: string;
  saleNumber?: string | null;
  /** Monotonic-ish stamp so stale messages can be ignored (ms since epoch). */
  at: number;
}

type Listener = (snap: DisplaySnapshot) => void;

function getChannel(): BroadcastChannel | null {
  if (typeof window === 'undefined' || typeof BroadcastChannel === 'undefined') return null;
  return new BroadcastChannel(CHANNEL);
}

/** Register-side: push the current snapshot to any open display windows. */
export function publishDisplay(snap: DisplaySnapshot): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(snap));
  } catch {
    /* private mode / quota — the BroadcastChannel still delivers live updates */
  }
  const ch = getChannel();
  if (ch) {
    ch.postMessage(snap);
    ch.close();
  }
}

/** Display-side: read the last published snapshot (for immediate first paint). */
export function readLastSnapshot(): DisplaySnapshot | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as DisplaySnapshot) : null;
  } catch {
    return null;
  }
}

/**
 * Display-side: subscribe to live snapshots. Returns an unsubscribe function.
 * Delivers via BroadcastChannel and, as a fallback, the cross-tab `storage`
 * event. Out-of-order messages (older `at`) are dropped.
 */
export function subscribeDisplay(onSnap: Listener): () => void {
  let lastAt = 0;
  const deliver = (snap: DisplaySnapshot | null): void => {
    if (!snap || typeof snap.at !== 'number' || snap.at < lastAt) return;
    lastAt = snap.at;
    onSnap(snap);
  };

  const ch = getChannel();
  const onMessage = (e: MessageEvent): void => deliver(e.data as DisplaySnapshot);
  ch?.addEventListener('message', onMessage);

  const onStorage = (e: StorageEvent): void => {
    if (e.key === STORAGE_KEY && e.newValue) {
      try {
        deliver(JSON.parse(e.newValue) as DisplaySnapshot);
      } catch {
        /* ignore malformed payloads */
      }
    }
  };
  window.addEventListener('storage', onStorage);

  return () => {
    ch?.removeEventListener('message', onMessage);
    ch?.close();
    window.removeEventListener('storage', onStorage);
  };
}
